import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, Check, X, Infinity as InfinityIcon, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { api } from '@/services/api';
import { ApiError } from '@/services/httpClient';
import { emitPlanChanged } from '@/lib/billingEvents';
import type { Plan, BillingPeriod, PlansResponse } from '@/types/billing';
import { AgencySubscriptionView } from './AgencySubscriptionView';
import { AgencyDiscoverySection } from './AgencyDiscoverySection';
import { AGENCY_SELF_SERVE_ENABLED } from '@/lib/productFlags';
import { MemberPlanInfoCard } from './MemberPlanInfoCard';
import { ScheduledPlanChangeBanner } from '@/components/billing/ScheduledPlanChangeBanner';

/**
 * Plan rank — Pricing v3.2 (21/05/26).
 * Legacy aliases (starter/growth/professional) coexist with v3.2 IDs
 * (sme_*) during the migration window. Both map to the same rank so the
 * upgrade/downgrade logic continues to work on documents that haven't
 * been backfilled yet.
 */
const PLAN_RANK: Record<string, number> = {
  freemium_trial: 0,
  // v3.2 SME tiers
  sme_starter: 1,
  sme_growth: 2,
  sme_professional: 3,
  // Legacy aliases (pre-v3.2)
  starter: 1,
  growth: 2,
  professional: 3,
};
const RECOMMENDED_PLAN = 'sme_growth';

/**
 * Display labels — Pricing v3.2. The plan-IDs starter/growth/professional
 * may still appear in legacy `Plan.planId` payloads from companies docs
 * that haven't been migrated; we map them to the same SME label so the FE
 * shows a consistent name regardless of the doc state.
 */
const PLAN_LABEL: Record<string, string> = {
  freemium_trial: 'Trial',
  sme_starter: 'Starter',
  sme_growth: 'Growth',
  sme_professional: 'Professional',
  // v3.2 Agency tiers
  agency_starter: 'Agency Starter',
  agency_medium: 'Agency Medium',
  agency_professional: 'Agency Professional',
  agency_enterprise: 'Agency Enterprise',
  // Legacy aliases (pre-v3.2)
  starter: 'Starter',
  growth: 'Growth',
  professional: 'Professional',
};

function planRank(id: string): number {
  return PLAN_RANK[id] ?? 0;
}

function normalizeCheckoutRedirectUrl(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  let t = input.trim();
  if (!t) return null;
  if (t.startsWith('//')) t = `${window.location.protocol}${t}`;
  try {
    const u = new URL(t);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.toString();
  } catch { return null; }
}

function formatNum(n: number | undefined): string {
  if (n === undefined || n === null) return '—';
  if (n >= 999999) return '∞';
  return n.toLocaleString('it-IT');
}

// v3.5 (25/05/26): rimossa `totalAiCredits(plan)` helper.
// Era `seats × aiUser` per il display "AI Credits totali / mese". Adesso il
// pool aiUser è eliminato; mostriamo direttamente `plan.limits.aiCompany`
// come "AI Credits / mese" (per-company, 150/500/1500).

function annualSavingsPercent(monthly: number, annual: number): number {
  if (!monthly || !annual) return 0;
  const fullYear = monthly * 12;
  const saved = fullYear - annual;
  return Math.round((saved / fullYear) * 100);
}

export function SubscriptionTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedCompany, user, refreshSession } = useAuth();
  const { t, language } = useLanguage();
  const queryClient = useQueryClient();

  // v3.4 (25/05/26) — Member confinement: invited_member users (or anyone the
  // BE marks `canManagePlan: false`) see a readonly card that tells them who
  // manages their plan. They can't checkout/portal/cancel either way (the BE
  // requirePlanOwner middleware enforces it), but the FE hides the CTAs so
  // there are no broken interactions.
  if (user && user.canManagePlan === false) {
    return <MemberPlanInfoCard owner={user.effectiveOwner ?? null} />;
  }

  // Phase 2 Agency: branch when current user is an agency owner.
  // The branch reads `user.accountType` from the AuthContext snapshot. After
  // a successful Stripe checkout that PROMOTED the user to agency_owner
  // (Discovery flow), the snapshot may still be stale until refreshSession()
  // fires — handled inside the checkout-success useEffect below.
  if (user?.accountType === 'agency_owner' && user.agency) {
    return <AgencySubscriptionView agency={user.agency} />;
  }

  // Defensive guard: an agency_owner without `agency` payload is in a
  // transient state (BE getMe returns the agency object only when both
  // accountType=agency_owner AND agencyPlanId are set on the user doc).
  // Showing the SME pricing grid here would be misleading — agency owners
  // must NOT be allowed to upgrade/downgrade to an SME tier (those are
  // segment-incompatible plans on a different per-recruiter billing model).
  // Trigger a single refresh attempt and render a clear pending state.
  if (user?.accountType === 'agency_owner' && !user.agency) {
    void refreshSession().catch(() => {});
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center space-y-3">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-primary/10">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-primary border-t-transparent" />
          </div>
          <p className="text-sm text-muted-foreground max-w-xs">
            {t('subscription.agency.loadingContext') /* added below */}
          </p>
        </div>
      </div>
    );
  }

  const companyId = selectedCompany?.id;
  const isAdmin = Boolean(companyId && user?.memberships[companyId]?.permission === 'admin');

  // Period state from URL ?period=
  const urlPeriod = (searchParams.get('period') === 'annual' ? 'annual' : 'monthly') as BillingPeriod;
  const [period, setPeriod] = useState<BillingPeriod>(urlPeriod);
  useEffect(() => { setPeriod(urlPeriod); }, [urlPeriod]);

  const setPeriodAndUrl = (p: BillingPeriod) => {
    setPeriod(p);
    const next = new URLSearchParams(searchParams);
    if (p === 'annual') next.set('period', 'annual'); else next.delete('period');
    setSearchParams(next, { replace: true });
  };

  // Highlight + auto-checkout deeplink
  const highlightedPlan = searchParams.get('plan');
  const autoUpgrade = searchParams.get('upgrade') === 'true';

  const [confirmPlan, setConfirmPlan] = useState<Plan | null>(null);
  const [checkoutPlanId, setCheckoutPlanId] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const checkoutBusy = checkoutPlanId !== null;

  // ── Plans (React Query) ───────────────────────────────────────────────
  const plansQuery = useQuery<PlansResponse>({
    queryKey: ['billing', 'plans', companyId, 'all'],
    queryFn: () => api.billing.listPlans(companyId!, { segment: 'all' }),
    enabled: !!companyId,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  // v3.5 (25/05/26): rimossa `usageQuery` (BillingUsageMe per-user pool deprecated).
  // v3.5 follow-up (26/05/26): rimossa anche `UsageSection` dal render
  // (ridondante con Dashboard CapUsageWidgets). `statusQuery` resta perché
  // ancora consumato da PlanCards (per evidenziare il piano attivo).

  const statusQuery = useQuery({
    queryKey: ['billing', 'status', companyId],
    queryFn: () => api.billing.getStatus(companyId!),
    enabled: !!companyId,
    staleTime: 60_000,
  });

  // ── Checkout success handler ──────────────────────────────────────────
  // Runs when Stripe redirects back with ?checkout=success after any
  // checkout flow (SME upgrade, Agency conversion, top-up).
  //
  // Three things must happen for the UI to reflect the new plan:
  //   1. BE reconcile  → Firestore `billing.planId` syncs to Autumn truth
  //                      (handles agency-owned companies via the owner
  //                      subscription, see companyPlan.ts).
  //   2. RQ invalidate → all `['billing', ...]` queries refetch.
  //   3. refreshSession → AuthContext snapshot picks up `accountType` and
  //                      `me.agency` so the SubscriptionTab branch above
  //                      switches to AgencySubscriptionView for users who
  //                      just converted from SME → Agency.
  //
  // Without (3) the user lands back on the SME pricing grid even though
  // their account is now agency_owner — they would have to refresh
  // manually to see Agency Starter/Pro.
  const checkoutFlag = searchParams.get('checkout');
  const checkoutPlan = searchParams.get('plan');
  useEffect(() => {
    if (checkoutFlag !== 'success' || !companyId) return;
    const targetPlan = checkoutPlan;
    void (async () => {
      try {
        // v3.5.2 polish (26/05/26): polling con retry per gestire la race
        // Stripe → Autumn → BE webhook. La prima `reconcile` può leggere
        // Autumn quando il webhook Stripe non è ancora arrivato → BE
        // ritorna il vecchio planId → UI mostra ancora il piano vecchio
        // finché l'utente non refresha. Adesso poll ogni 2s per max 20s
        // finché `currentPlanId` corrisponde a `targetPlan` (o timeout).
        const POLL_INTERVAL_MS = 2000;
        const POLL_MAX_ATTEMPTS = 10; // 20s totali
        let confirmed = false;
        for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
          await api.billing.reconcile(companyId);
          await queryClient.invalidateQueries({ queryKey: ['billing'] });
          // Leggi status fresco (post-invalidate) per validare l'upgrade
          try {
            const status = await api.billing.getStatus(companyId);
            if (!targetPlan || status?.planId === targetPlan) {
              confirmed = true;
              break;
            }
          } catch {
            // ignore, ritento al prossimo loop
          }
          if (attempt < POLL_MAX_ATTEMPTS - 1) {
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          }
        }
        // Refresh sessione utente (accountType + agency)
        await refreshSession();
        // Sprint 2 (S2.E): broadcast so non-billing surfaces can drop their
        // stale derived state (PENDING_UPGRADE counters, positions cap
        // pre-check cache, AI 402 toasts in floating chats, etc.).
        emitPlanChanged({ source: 'checkout-success', planId: targetPlan ?? undefined });
        // Forza un ulteriore invalidate per refresh dei widget billing-status
        await queryClient.invalidateQueries({ queryKey: ['billing'] });

        if (confirmed) {
          toast.success(
            t('subscription.checkout.success_toast', {
              plan: PLAN_LABEL[targetPlan ?? ''] ?? (targetPlan ?? '—'),
            })
          );
        } else {
          // Timeout — l'upgrade è andato OK su Stripe ma Autumn/BE non ha
          // ancora propagato. UI mostrerà il nuovo piano entro ~30s al
          // prossimo poll automatico di useBillingStatus.
          toast.success(
            t('subscription.checkout.applied'),
            { description: t('subscription.checkout.success_toast', {
              plan: PLAN_LABEL[targetPlan ?? ''] ?? (targetPlan ?? '—'),
            }) },
          );
        }
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : (e as Error).message;
        toast.error(t('subscription.checkout.error'), { description: msg });
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete('checkout');
        next.delete('upgrade');
        setSearchParams(next, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutFlag, companyId]);

  // After-portal reconcile (existing behaviour)
  const fromPortalFlag = searchParams.get('fromPortal');
  useEffect(() => {
    if (fromPortalFlag !== '1' || !companyId) return;
    void (async () => {
      try {
        await api.billing.reconcile(companyId);
        await queryClient.invalidateQueries({ queryKey: ['billing'] });
        // Sprint 2 (S2.E): plan may have changed via Stripe Customer Portal.
        emitPlanChanged({ source: 'portal-return' });
        // Sprint 2 Block 2 Fix H (22/05/26 ~18:05): refresh AuthContext
        // snapshot too. Otherwise ProfileHeader / TrialBanner / sidebar
        // badges read stale `accountType` / `agencyPlanId` / `agency.*`
        // even though the billing tab itself shows the right plan.
        // Without this call, an agency owner who cancels via Stripe
        // portal sees "Agency Owner" badge with "Plan: Trial" for up
        // to one session (until the next /users/me fetch).
        await refreshSession();
      } catch { /* ignore */ }
      const next = new URLSearchParams(searchParams);
      next.delete('fromPortal');
      setSearchParams(next, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromPortalFlag, companyId]);

  // ── Auto-upgrade deeplink ─────────────────────────────────────────────
  // When ?upgrade=true&plan=X, route the user as if they clicked the CTA on
  // that plan: direct checkout for upgrades, downgrade-confirm modal otherwise.
  useEffect(() => {
    if (!autoUpgrade || !plansQuery.data || !highlightedPlan) return;
    const target = plansQuery.data.plans.find(p => p.planId === highlightedPlan);
    if (!target || target.planId === 'freemium_trial' || target.isCurrent) return;
    if (target.segment === 'agency') return;
    const targetRank = planRank(target.planId);
    const currentRank = planRank(plansQuery.data.currentPlanId);
    if (targetRank > currentRank) {
      void startCheckout(target);
    } else if (targetRank < currentRank && targetRank > 0) {
      setConfirmPlan(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoUpgrade, plansQuery.data, highlightedPlan]);

  // ── Handlers ───────────────────────────────────────────────────────────
  // v3.5.2 polish (26/05/26): tab name 'subscription' era stale dall'epoca
  // /settings?tab=subscription. Oggi la SubscriptionTab vive in /profile
  // come tab 'billing'. Senza questo fix, dopo il return da Stripe
  // checkout l'utente atterrava su ?tab=subscription che ProfilePage NON
  // riconosce come valido → fallback a tab 'account' → SubscriptionTab
  // mai montata → useEffect checkout-success mai eseguito → UI stale
  // finché l'utente non refresha.
  const buildSuccessUrl = (planId: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', 'billing');
    url.searchParams.set('checkout', 'success');
    url.searchParams.set('plan', planId);
    url.searchParams.delete('upgrade');
    return url.toString();
  };

  const portalReturnUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', 'billing');
    url.searchParams.set('fromPortal', '1');
    url.searchParams.delete('checkout');
    return url.toString();
  };

  const startCheckout = async (plan: Plan) => {
    if (!companyId) return;
    if (!isAdmin) {
      toast.error(t('subscription.portal.adminOnly'));
      return;
    }
    setCheckoutPlanId(plan.planId);
    try {
      const successUrl = buildSuccessUrl(plan.planId);
      const raw = await api.billing.checkout(companyId, {
        planId: plan.planId,
        billingPeriod: period,
        successUrl,
      });
      const payload = raw as { checkoutUrl?: string | null; checkout_url?: string | null };
      const href = normalizeCheckoutRedirectUrl(payload.checkoutUrl ?? payload.checkout_url);
      if (!href) {
        // Instant update, no redirect
        toast.success(t('subscription.checkout.applied'));
        await queryClient.invalidateQueries({ queryKey: ['billing'] });
        // Sprint 2 (S2.E)
        emitPlanChanged({ source: 'instant-checkout', planId: plan.planId });
        setConfirmPlan(null);
        return;
      }
      window.location.assign(href);
    } catch (e) {
      const apiErr = e instanceof ApiError ? e : null;
      if (apiErr?.status === 423) {
        toast.error(t('subscription.checkout.threeDS_required'));
      } else {
        const msg = apiErr?.message ?? (e as Error).message;
        toast.error(t('subscription.checkout.error'), { description: msg });
      }
    } finally {
      setCheckoutPlanId(null);
    }
  };

  const onPlanCtaClick = (plan: Plan, isDowngrade: boolean) => {
    if (checkoutBusy || portalLoading) return;
    if (isDowngrade) {
      setConfirmPlan(plan);
    } else {
      void startCheckout(plan);
    }
  };

  const openPortal = async () => {
    if (!companyId || !isAdmin || checkoutBusy) return;
    setPortalLoading(true);
    try {
      const { url } = await api.billing.getPortalUrl(companyId, portalReturnUrl());
      window.location.href = url;
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      toast.error(t('subscription.checkout.error'), { description: msg });
    } finally {
      setPortalLoading(false);
    }
  };

  // ── Render guards ──────────────────────────────────────────────────────
  if (!companyId) {
    return <p className="text-sm text-muted-foreground">Select a company to view subscription.</p>;
  }
  if (plansQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground text-sm py-8">
        <Loader2 className="h-4 w-4 animate-spin" /> {t('subscription.loading')}
      </div>
    );
  }
  if (plansQuery.error || !plansQuery.data) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">{t('subscription.loadError')}</p>
        <Button variant="outline" size="sm" onClick={() => plansQuery.refetch()}>
          {t('subscription.retry')}
        </Button>
      </div>
    );
  }

  const { plans, currentPlanId } = plansQuery.data;
  const smePlans = plans.filter(p => p.segment === 'sme');
  const agencyPlans = plans.filter(p => p.segment === 'agency');
  const currentPlan = smePlans.find(p => p.planId === currentPlanId);
  const currentRank = planRank(currentPlanId);
  const billingStatus = statusQuery.data;
  const scheduled = billingStatus?.scheduled;
  const cancelAtPeriodEnd = !scheduled ? billingStatus?.cancelAtPeriodEnd : undefined;

  return (
    <div className="space-y-8">
      <ScheduledPlanChangeBanner
        scheduled={scheduled}
        cancelAtPeriodEnd={cancelAtPeriodEnd}
        currentPlanLabel={PLAN_LABEL[currentPlanId] ?? currentPlanId}
        owner={billingStatus?.owner}
        language={language}
      />

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold">{t('subscription.title')}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t('subscription.currentPlan')}:{' '}
            <span className="font-medium text-foreground">
              {PLAN_LABEL[currentPlanId] ?? currentPlanId}
            </span>
            {/* v3.2 (21/05/26): "(Annuale)" badge dropped — annual cycle removed. */}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={openPortal}
          disabled={!isAdmin || portalLoading || checkoutBusy}
          aria-label={t('subscription.portal.cta')}
        >
          {portalLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-2" />}
          {t('subscription.portal.cta')}
        </Button>
      </div>

      {/*
        v3.2 (21/05/26) — Period toggle removed (annual cycle dropped).
        The PeriodToggle component is kept defined below as dead code for
        possible re-enable; remove it entirely in Fase 5 cleanup.
      */}

      {/* Plan cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {smePlans.map(plan => (
          <PlanCard
            key={plan.planId}
            plan={plan}
            period={period}
            currentRank={currentRank}
            highlighted={highlightedPlan === plan.planId}
            onCta={onPlanCtaClick}
            checkoutPlanId={checkoutPlanId}
            checkoutDisabled={checkoutBusy || portalLoading}
          />
        ))}
      </div>

      {/* Compare table */}
      <CompareTable plans={smePlans} />

      {/* v3.5 follow-up (26/05/26 14:15): rimossa <UsageSection> dal tab
          Billing SME. Era ridondante — Dashboard (`CapUsageWidgets`) mostra
          già gli stessi pool con UI più ricca (semafori, link upgrade), e
          esponeva pool non rilevanti per SME (es. `client_companies` agency,
          `transcripts` 0/0 quando non c'è allocation dedicata). */}

      {/* Agency discovery — visible to SME (individual) users only */}
      {AGENCY_SELF_SERVE_ENABLED && agencyPlans.length > 0 && (
        <AgencyDiscoverySection
          plans={agencyPlans}
          companyId={companyId}
          actionsDisabled={checkoutBusy || portalLoading}
        />
      )}

      {/* Confirm downgrade dialog */}
      <AlertDialog
        open={!!confirmPlan}
        onOpenChange={(v) => { if (!v && !checkoutBusy) setConfirmPlan(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('subscription.checkout.downgrade_confirm_title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('subscription.checkout.downgrade_confirm_body', {
                from: PLAN_LABEL[currentPlanId] ?? currentPlanId,
                to: confirmPlan ? (PLAN_LABEL[confirmPlan.planId] ?? confirmPlan.planId) : '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={checkoutBusy}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={checkoutBusy}
              onClick={(e) => {
                e.preventDefault();
                if (confirmPlan) void startCheckout(confirmPlan);
              }}
            >
              {checkoutBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {checkoutBusy ? t('subscription.checkout.processing') : t('common.save')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Subcomponents
// ──────────────────────────────────────────────────────────────────────────

function PeriodToggle({
  period, onChange, plans,
}: { period: BillingPeriod; onChange: (p: BillingPeriod) => void; plans: Plan[] }) {
  const { t } = useLanguage();
  const annualPlan = plans.find(p => p.hasAnnual && p.priceMonthly > 0 && p.priceAnnual);
  const savePct = annualPlan ? annualSavingsPercent(annualPlan.priceMonthly, annualPlan.priceAnnual!) : 0;

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <ToggleGroup
        type="single"
        value={period}
        onValueChange={(v) => v && onChange(v as BillingPeriod)}
        className="bg-muted rounded-md p-1"
      >
        <ToggleGroupItem
          value="monthly"
          aria-pressed={period === 'monthly'}
          className="text-xs px-4 data-[state=on]:bg-background data-[state=on]:shadow-sm"
        >
          {t('subscription.toggle.monthly')}
        </ToggleGroupItem>
        <ToggleGroupItem
          value="annual"
          aria-pressed={period === 'annual'}
          className="text-xs px-4 data-[state=on]:bg-background data-[state=on]:shadow-sm"
        >
          {t('subscription.toggle.annual')}
        </ToggleGroupItem>
      </ToggleGroup>
      {savePct > 0 && (
        <Badge variant="secondary" className="bg-green-100 text-green-900 border-green-200">
          {t('subscription.toggle.annualSave', { percent: savePct })}
        </Badge>
      )}
    </div>
  );
}

function PlanCard({
  plan, period, currentRank, highlighted, onCta, checkoutPlanId, checkoutDisabled,
}: {
  plan: Plan;
  period: BillingPeriod;
  currentRank: number;
  highlighted: boolean;
  onCta: (plan: Plan, isDowngrade: boolean) => void;
  checkoutPlanId: string | null;
  checkoutDisabled: boolean;
}) {
  const { t } = useLanguage();
  const isTrial = plan.planId === 'freemium_trial';
  const rank = planRank(plan.planId);
  const isUpgrade = rank > currentRank;
  const isDowngrade = rank < currentRank && rank > 0;
  const isCurrent = plan.isCurrent;

  // Pricing display
  //
  // Pricing v3.1.1 (21/05/26): la card Trial mostra "Gratis / 7gg" inline
  // come prezzo (allineata visivamente alle card "€XX / mese"). Il badge
  // separato "{n} giorni gratuiti" sotto il titolo è stato rimosso per
  // evitare ridondanza con il prezzo.
  let priceLine: React.ReactNode;
  let billedNote: React.ReactNode = null;
  if (isTrial) {
    priceLine = (
      <>
        <span className="text-3xl font-bold">{t('subscription.card.priceTrial')}</span>
        <span className="text-sm text-muted-foreground">
          {t('subscription.card.priceTrialPeriod', { days: plan.trialDays ?? 7 })}
        </span>
      </>
    );
  } else if (plan.priceMonthly === 0) {
    priceLine = <span className="text-3xl font-bold">{t('subscription.priceFree')}</span>;
  } else if (period === 'annual' && plan.hasAnnual && plan.priceAnnual) {
    const monthlyEq = Math.round(plan.priceAnnual / 12);
    priceLine = (
      <>
        <span className="text-3xl font-bold">€{monthlyEq}</span>
        <span className="text-sm text-muted-foreground">{t('subscription.perMonth')}</span>
      </>
    );
    billedNote = (
      <p className="text-xs text-muted-foreground mt-1">
        {t('subscription.perYearBilled', { total: plan.priceAnnual })}
      </p>
    );
  } else {
    priceLine = (
      <>
        <span className="text-3xl font-bold">€{plan.priceMonthly}</span>
        <span className="text-sm text-muted-foreground">{t('subscription.perMonth')}</span>
      </>
    );
  }

  // CTA — special-case the freemium_trial card.
  //
  // The trial is auto-attached at signup and CANNOT be selected manually
  // (you can't downgrade from a paid plan back to the 7-day trial). So
  // the button label depends on context:
  //
  //   - Trial is the current plan  → "Trial active" (disabled, primary)
  //   - Trial is NOT the current plan (user is on Starter/Growth/Pro)
  //     → "Free trial included" (disabled, muted)
  //
  // The previous code reused `cta_current` ("Current plan") for the
  // !isCurrent trial branch, which made the Trial card LOOK like the
  // current plan even when the user was actually on Starter — confusing
  // duplicate "Current plan" badges in the grid.
  let ctaLabel: string;
  let ctaDisabled = false;
  if (isTrial) {
    ctaLabel = isCurrent
      ? t('subscription.card.cta_trial_active')
      : t('subscription.card.cta_trial_included');
    ctaDisabled = true;
  } else if (isCurrent) {
    ctaLabel = t('subscription.card.cta_current');
    ctaDisabled = true;
  } else if (isDowngrade) {
    ctaLabel = t('subscription.card.cta_downgrade');
  } else {
    ctaLabel = t('subscription.card.cta_upgrade');
  }

  const isRecommended = plan.planId === RECOMMENDED_PLAN;
  const isThisLoading = checkoutPlanId === plan.planId;
  const buttonDisabled = ctaDisabled || checkoutDisabled;

  return (
    <Card
      role="article"
      aria-label={PLAN_LABEL[plan.planId] ?? plan.planId}
      className={cn(
        'relative p-5 flex flex-col gap-4 transition-all',
        isCurrent && 'border-primary ring-1 ring-primary/30',
        highlighted && !isCurrent && 'ring-2 ring-primary scale-[1.02]',
        isRecommended && !isCurrent && 'border-primary/40',
      )}
    >
      {isRecommended && !isCurrent && (
        <Badge className="absolute -top-2 left-4 bg-primary text-primary-foreground">
          {t('subscription.card.recommended')}
        </Badge>
      )}
      {isCurrent && (
        <Badge className="absolute -top-2 right-4 bg-primary text-primary-foreground">
          {t('subscription.card.activeBadge')}
        </Badge>
      )}

      <div>
        <h3 className="text-base font-semibold">{PLAN_LABEL[plan.planId] ?? plan.planId}</h3>
      </div>

      <div>
        <div className="flex items-baseline gap-1">{priceLine}</div>
        {billedNote}
      </div>

      {/*
        Pricing v3.1.1 (21/05/26) — bullet list:
        v3.5 (25/05/26 — Split Pools):
        - "Candidati AI" = pool `aiCompany` historicamente, ora pool dedicato
          `candidates` (300/1500/5000). Gate CV screening.
        - "AI Credits" = pool `aiCompany` ridotto (150/500/1500). Gate
          chat/transcript/autofill/interview-report. Non più `seats × aiUser`.
      */}
      <ul className="text-xs space-y-1.5 text-muted-foreground flex-1">
        <li>· {t('subscription.limits.seats', { n: formatNum(plan.limits.seats) })}</li>
        <li>· {t('subscription.limits.positions', { n: formatNum(plan.limits.positions) })}</li>
        <li>
          ·{' '}
          {isTrial
            ? t('subscription.limits.candidatesNoMonth', { n: formatNum(plan.limits.candidates) })
            : t('subscription.limits.candidates', { n: formatNum(plan.limits.candidates) })}
        </li>
        <li>
          ·{' '}
          {isTrial
            ? t('subscription.limits.aiCompany', { n: formatNum(plan.limits.aiCompany) })
            : t('subscription.limits.aiCompanyMonthly', { n: formatNum(plan.limits.aiCompany) })}
        </li>
      </ul>

      <Button
        size="sm"
        variant={isUpgrade ? 'default' : 'outline'}
        disabled={buttonDisabled}
        onClick={() => onCta(plan, isDowngrade)}
        aria-label={`${ctaLabel} ${PLAN_LABEL[plan.planId] ?? plan.planId}`}
        aria-busy={isThisLoading}
      >
        {isThisLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
        {isThisLoading ? t('subscription.checkout.processing') : ctaLabel}
      </Button>
    </Card>
  );
}

function CompareTable({ plans }: { plans: Plan[] }) {
  const { t } = useLanguage();
  // v3.5 (25/05/26 — Split Pools): la tabella include sia `candidates`
  // (300/1500/5000, gate CV screening) sia `aiCompany` (150/500/1500,
  // gate chat/transcript/autofill/interview-report). Erano nascosti in
  // v3.4 perché il pool era unico — adesso devono essere chiari per
  // permettere il confronto tra piani.
  // La riga "Call Transcription" è feature-gated solo su Starter+; Trial = ❌.
  // La riga Voice AI è stata rimossa: la feature è roadmap 2027 (SSOT §5.1).
  const rows: Array<{ key: keyof Plan['limits']; label: string }> = [
    { key: 'positions', label: t('subscription.compare.row.positions') },
    { key: 'candidates', label: t('subscription.compare.row.candidates') },
    { key: 'aiCompany', label: t('subscription.compare.row.aiCompany') },
    { key: 'seats', label: t('subscription.compare.row.seats') },
  ];

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">{t('subscription.compare.title')}</h3>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="bg-muted/50 text-xs">
              <th className="text-left p-3 font-medium">{t('subscription.compare.feature')}</th>
              {plans.map(p => (
                <th key={p.planId} className="text-center p-3 font-medium">
                  {PLAN_LABEL[p.planId] ?? p.planId}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.key} className="border-t border-border">
                <td className="p-3 text-muted-foreground">{row.label}</td>
                {plans.map(p => {
                  const v = p.limits[row.key] ?? 0;
                  return (
                    <td key={p.planId} className="p-3 text-center">
                      {v === 0 ? (
                        <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                      ) : v >= 999999 ? (
                        <InfinityIcon className="h-4 w-4 text-primary mx-auto" />
                      ) : (
                        <span className="text-xs font-medium">{formatNum(v)}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {/* Call transcription — Starter+ feature (commercial gate). Trial = ❌. */}
            <tr className="border-t border-border">
              <td className="p-3 text-muted-foreground">
                {t('subscription.compare.row.callTranscription')}
              </td>
              {plans.map(p => (
                <td key={p.planId} className="p-3 text-center">
                  {p.planId === 'freemium_trial' ? (
                    <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                  ) : (
                    <Check className="h-4 w-4 text-primary mx-auto" />
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/*
        Explainer block — clarifies the two AI quotas shown on the cards.
        "Candidati AI" is the AI-processing cap (pool aiCompany / candidates);
        "AI Credits" is the conversational AI cap (pool aiUser × seats).
      */}
      <div className="rounded-md border border-border bg-muted/30 p-4 space-y-2 text-xs text-muted-foreground">
        <p>
          <strong className="text-foreground">
            {t('subscription.compare.creditsExplainer.title')}
          </strong>
        </p>
        <p>{t('subscription.compare.creditsExplainer.candidatesAi')}</p>
        <p>{t('subscription.compare.creditsExplainer.aiCredits')}</p>
      </div>
    </div>
  );
}

// v3.5 follow-up (26/05/26): `UsageSection` + `humanLabel` + `HIDDEN_USAGE_POOLS`
// rimossi. Erano usati solo dal blocco "Utilizzo (questo mese)" della tab
// Billing SME, dichiarato ridondante dall'utente (la Dashboard già mostra
// gli stessi pool tramite CapUsageWidgets, con UX più ricca: semafori,
// link upgrade, filtro per plan).
//
// Se in futuro serve, il widget di Dashboard è già responsivo e può
// essere mostrato in qualsiasi pagina con <CapUsageWidgets />.

export default SubscriptionTab;
